import { createSelector } from '@ngrx/store';
import { selectEntityTypes } from '../../../core/store/entity-types/entity-types.selectors';
import type { EntityType } from '../../../core/models/entity-type.model';
import type { PropertyItem } from '../../../core/models/property.model';
import {
  selectCurrentProjectEntities,
  selectProjectEntityNotes,
  selectProjectEntityTree,
  selectProjectFeedbackMessages
} from '../../store/project-data/project-data.selectors';
import { EntityTypeKey } from '../../../shared/constants/entity-type-keys';
import type { Entity, TreeEntity } from '../../../core/models/entity.model';
import {
  selectMultiEditEnabled,
  selectMultiEditEntityTypeKey,
  selectMultiEditSelectionStatuses
} from '../../store/product-view/product-view.selectors';

const getProductTreeProductDepth = (product: Entity, products: Entity[]): number => {
  let depth = 0;

  while (product?.ParentId) {
    product = products.find(p => p.Id === product.ParentId);
    if (product) {
      depth++;
    }
  }

  return depth;
};

export interface PropertyItemWithDepth {
  item: PropertyItem;
  depth: number;
}

export const selectProductTreeMultiEditTypesAsItemsWithDepth = createSelector(
  selectCurrentProjectEntities,
  selectEntityTypes,
  (entities, types): PropertyItemWithDepth[] => {
    if (Object.keys(types).length === 0) {
      return [];
    }

    const typesInProject: { type: EntityType; depth: number }[] = [];

    entities.forEach(entity => {
      const type = types[entity.EntityTypeKey];

      if (
        !type ||
        type.Key === EntityTypeKey.Project ||
        type.Key === EntityTypeKey.GastroFlowHoodLine ||
        type.Key === EntityTypeKey.GastroFlowHoodConnectionLine
      ) {
        return;
      }

      if (!typesInProject.find(addedType => addedType.type.Key == type.Key)) {
        const depth = getProductTreeProductDepth(entity, entities);
        typesInProject.push({ type, depth });
      }
    });

    return typesInProject.map(type => ({
      item: {
        Value: type.type.Key,
        NameId: type.type.NameId
      },
      depth: type.depth
    }));
  }
);

export const selectProductTreeMessages = createSelector(
  selectProjectFeedbackMessages,
  selectProjectEntityNotes,
  (messages, notes): TreeEntity[] => {
    const result = [];

    messages.forEach(msg => {
      result.push(msg);
    });
    notes.forEach(note => {
      if (note.property.Value) {
        result.push(note.entity);
      }
    });

    return result;
  }
);

/*
export const selectProductTreeMessageIcons = createSelector(
  selectProjectFeedbackMessages,
  selectProjectEntityNotes,
  (messages, notes): Map<string, Set<number>> => {
    const result = new Map<string, Set<number>>();

    messages.forEach(msg => {
      addMessageToIconCollection(msg.Parent, result, msg.Properties['Level'].Value as number);
    });
    notes.forEach(note => {
      if (note.property.Value) {
        addMessageToIconCollection(note.entity, result, 2);
      }
    });

    return result;
  }
);*/

/**
 * Selector which selects the products to be displayed in the product tree in the current state configuration
 */
export const selectProductPageProductTreeData = createSelector(
  selectProjectEntityTree,
  selectMultiEditEnabled,
  selectMultiEditEntityTypeKey,
  selectMultiEditSelectionStatuses,
  (projectTree, multiEditEnabled, multiEditEntityTypeKey, multiEditSelectionStatuses) => {
    if (!projectTree) {
      return null;
    }

    // Recursively goes through the project tree and creates a clone where all the products in the tree have been filtered.
    // The tree will only include products which should be shown in the tree.
    const cloneAndFilterRecursive = (entity: TreeEntity): TreeEntity => {
      // Create clone of the entity
      const newEntity: TreeEntity = { ...entity };

      // Filter the children and create clones of them recursively
      newEntity.Children = entity.Children.filter(child => {
        if (
          child.EntityTypeKey === EntityTypeKey.Message ||
          child.EntityTypeKey === EntityTypeKey.GastroFlowHoodLine ||
          child.EntityTypeKey == EntityTypeKey.GastroFlowHoodConnectionLine
        ) {
          return false;
        }

        // Multi-select mode filtering. Only include products which are selectable or have selectable children
        if (multiEditEnabled && multiEditEntityTypeKey) {
          const multiSelectStatus = multiEditSelectionStatuses[child.Id];
          if (!multiSelectStatus) {
            return false;
          }
          return multiSelectStatus.selectable;
        }

        return true;
      }).map(child => {
        const clonedChild = cloneAndFilterRecursive(child);
        clonedChild.Parent = newEntity; // Important: set the parent reference correctly!
        return clonedChild;
      });

      return newEntity;
    };

    // Call the recursive function
    return cloneAndFilterRecursive(projectTree);
  }
);
